import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import AccountMark from './AccountMark';

/**
 * Add a reminder in one line: who, what, when.
 *
 * Owner, Sep 18 2026: his phone had "Reach out to Ginger / Percy / Stephany"
 * with nowhere to live. This puts them on the customer with a date, so they
 * come back on My Day and in Needs Attention when the day arrives.
 */
export const WHEN = [
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'week', label: 'Next week' },
  { key: 'month', label: 'Next month' },
  { key: 'spring', label: 'Spring' },
  { key: 'fall', label: 'Fall' },
];

export default function ReminderAdd({ customerId, customerName, compact = false, onAdded }: {
  customerId?: number | null; customerName?: string | null; compact?: boolean; onAdded?: (r: any) => void;
}) {
  const [who, setWho] = useState<{ id: number; name: string } | null>(customerId ? { id: customerId, name: customerName || '' } : null);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'reach_out' | 'future_work'>('reach_out');
  const [note, setNote] = useState('');
  const [when, setWhen] = useState<string>('tomorrow');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const { data: people } = useQuery('reminder-people', () => api.get('/quotes/pickers/customers').then(r => (Array.isArray(r.data) ? r.data : r.data?.customers || [])),
    { enabled: !customerId, staleTime: 300000 });
  const needle = search.trim().toLowerCase();
  const found = !customerId && needle.length > 1 ? (people || []).filter((c: any) => String(c.name || '').toLowerCase().includes(needle)).slice(0, 5) : [];

  async function add() {
    if (!who) { toast.error('Pick who this is about'); return; }
    setBusy(true);
    try {
      const r = await api.post('/reminders', { customer_id: who.id, kind, note: note.trim(), when: date || when });
      toast.success(r.data?.message || 'Noted');
      setNote(''); setDate(''); if (!customerId) { setWho(null); setSearch(''); }
      onAdded?.(r.data?.reminder);
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Could not save that'); }
    finally { setBusy(false); }
  }

  const chip = (on: boolean) => `px-2.5 py-1 rounded-full border text-xs ${on ? 'press bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`;
  const input = compact ? 'border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm' : 'input text-sm';
  return (
    <div className="space-y-2">
      {!customerId && (
        <div>
          {who ? (
            <p className="text-sm text-gray-800 flex items-center gap-2"><span className="font-medium">{who.name}</span><button type="button" onClick={() => { setWho(null); setSearch(''); }} className="text-xs text-gray-500 underline">change</button></p>
          ) : (
            <>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Who? Start typing a name" className={`${input} w-full`} />
              {found.map((c: any) => (
                <button key={c.id} type="button" onClick={() => { setWho({ id: c.id, name: c.name }); setSearch(''); }}
                  className="block w-full text-left px-3 py-2 text-sm border-b border-gray-100 hover:bg-brand-50">{c.name}<AccountMark mark={c.account_mark} />{c.address ? <span className="text-gray-500"> · {c.address}</span> : null}</button>
              ))}
            </>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setKind('reach_out')} className={chip(kind === 'reach_out')}>Reach out</button>
        <button type="button" onClick={() => setKind('future_work')} className={chip(kind === 'future_work')}>Future work</button>
      </div>
      <input value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }}
        placeholder={kind === 'reach_out' ? 'About what? (top dressing, the bid, a call back)' : 'What do they want later? (front bed refreshed)'} className={`${input} w-full`} />
      <div className="flex flex-wrap items-center gap-1.5">
        {WHEN.map(w => <button key={w.key} type="button" onClick={() => { setWhen(w.key); setDate(''); }} className={chip(!date && when === w.key)}>{w.label}</button>)}
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="border border-gray-300 rounded-full px-2 py-1 text-xs" title="Pick a date" />
        <button type="button" onClick={add} disabled={busy || !who} className="press ml-auto text-xs px-3 py-1.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">{busy ? '…' : 'Remind me'}</button>
      </div>
    </div>
  );
}
